import type { ActivityType } from '../services/activityService';
import { ACTIVITY_TYPES, logUserActivity } from '../services/api';

const EMPTY_OBJECT: Record<string, never> = {};

const EMPTY_ARRAY: never[] = [];

export { ACTIVITY_TYPES };

interface ActivityMetadata {
  source?: string;
  userAgent?: string;
  url?: string;
  timestamp?: string;
  [key: string]: unknown;
}

interface ActivityData {
  timestamp?: string;
  [key: string]: unknown;
}

interface ActivityResult {
  success: boolean;
  error?: string;
}

interface TenderData {
  id?: string;
  title?: string;
  type?: string;
  deadline?: string;
  source?: string;
  updateType?: string;
  reason?: string;
  fileName?: string;
  fileCount?: number;
}

interface DocumentData {
  type?: string;
  fileName?: string;
  fileSize?: number;
  location?: string;
  source?: string;
}

interface AIData {
  serviceName: string;
  serviceType: string;
  inputType: string;
  processingTime?: number;
  success?: boolean;
}

interface SearchData {
  type?: string;
  query?: string;
  resultsCount?: number;
  filters?: Record<string, unknown>;
  location?: string;
}

interface PageData {
  pageName: string;
  pageUrl?: string;
  referrer?: string;
  sessionDuration?: number;
}

interface SettingsData {
  settingsType: string;
  settingsSection: string;
  changedFields?: string[];
  previousValues?: Record<string, unknown>;
}

interface ErrorData {
  errorType?: string;
  errorMessage: string;
  errorCode?: string;
  errorLocation?: string;
  userAction?: string;
  stackTrace?: string | null;
}

interface SessionData {
  sessionId?: string;
  duration?: number;
  endReason?: string;
}

interface BatchedActivity {
  activityType: ActivityType;
  data: ActivityData;
  metadata: ActivityMetadata;
}

export const logActivity = async (
  activityType: ActivityType,
  data: ActivityData = {},
  metadata: ActivityMetadata = {}
): Promise<ActivityResult> => {
  try {
    const timestamp = new Date().toISOString();

    const result = await logUserActivity(
      activityType,
      {
        ...data,
        timestamp,
      },
      {
        source: 'frontend',
        userAgent: navigator.userAgent,
        url: window.location.href,
        ...metadata,
        timestamp,
      }
    );

    return {
      success: !!result,
      error: result.error ? String(result.error) : '',
    };
  } catch (error: unknown) {
    const err = error as Error;
    return { success: false, error: err.message };
  }
};

export const logTenderActivity = {
  created: (tenderData: TenderData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.TENDER_CREATED, {
      tenderTitle: tenderData.title,
      tenderType: tenderData.type,
      deadline: tenderData.deadline,
      source: tenderData.source || 'manual',
    }),

  updated: (tenderData: TenderData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.TENDER_UPDATED, {
      tenderId: tenderData.id,
      tenderTitle: tenderData.title,
      updateType: tenderData.updateType || 'general',
      reason: tenderData.reason,
    }),

  deleted: (tenderData: TenderData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.TENDER_DELETED, {
      tenderId: tenderData.id,
      tenderTitle: tenderData.title,
      deletionReason: tenderData.reason || 'user_action',
    }),

  viewed: (tenderData: TenderData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.SEARCH_PERFORMED, {
      searchType: 'tender_view',
      tenderId: tenderData.id,
      tenderTitle: tenderData.title,
    }),

  downloaded: (tenderData: TenderData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.DOCUMENT_DOWNLOADED, {
      documentType: 'tender_document',
      tenderId: tenderData.id,
      fileName: tenderData.fileName,
    }),
};

export const logDocumentActivity = {
  uploaded: (docData: DocumentData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.DOCUMENT_UPLOADED, {
      documentType: docData.type || 'general',
      fileName: docData.fileName,
      fileSize: docData.fileSize,
      uploadLocation: docData.location || 'unknown',
    }),

  downloaded: (docData: DocumentData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.DOCUMENT_DOWNLOADED, {
      documentType: docData.type || 'general',
      fileName: docData.fileName,
      downloadSource: docData.source || 'unknown',
    }),

  deleted: (docData: DocumentData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.DOCUMENT_DELETED, {
      documentType: docData.type || 'general',
      fileName: docData.fileName,
      deleteSource: docData.source || 'unknown',
    }),

  regenerated: (docData: DocumentData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.DOCUMENT_REGENERATED, {
      documentType: docData.type || 'general',
      fileName: docData.fileName,
      source: docData.source || 'unknown',
    }),

  updated: (docData: DocumentData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.DOCUMENT_UPDATED, {
      documentType: docData.type || 'general',
      fileName: docData.fileName,
      updateSource: docData.source || 'unknown',
    }),

  reviewed: (docData: DocumentData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.DOCUMENT_REVIEWED, {
      documentType: docData.type || 'general',
      fileName: docData.fileName,
      source: docData.source || 'unknown',
    }),
};

export const logAIActivity = {
  serviceUsed: (aiData: AIData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.AI_SERVICE_USED, {
      serviceName: aiData.serviceName,
      serviceType: aiData.serviceType,
      inputType: aiData.inputType,
      processingTime: aiData.processingTime,
      success: aiData.success !== false,
    }),
};

export const logSearchActivity = {
  performed: (searchData: SearchData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.SEARCH_PERFORMED, {
      searchType: searchData.type || 'general',
      searchQuery: searchData.query,
      resultsCount: searchData.resultsCount,
      filters: searchData.filters || EMPTY_OBJECT,
      searchLocation: searchData.location || 'unknown',
    }),
};

export const logNavigationActivity = {
  pageVisit: (pageData: PageData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.SEARCH_PERFORMED, {
      searchType: 'page_navigation',
      pageName: pageData.pageName,
      pageUrl: pageData.pageUrl || window.location.href,
      referrer: document.referrer || null,
      sessionDuration: pageData.sessionDuration || null,
    }),
};

export const logSettingsActivity = {
  updated: (settingsData: SettingsData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.SETTINGS_UPDATED, {
      settingsType: settingsData.settingsType,
      settingsSection: settingsData.settingsSection,
      changedFields: settingsData.changedFields || EMPTY_ARRAY,
      previousValues: settingsData.previousValues || EMPTY_OBJECT,
    }),
};

export const logErrorActivity = {
  occurred: (errorData: ErrorData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.ERROR_OCCURRED, {
      errorType: errorData.errorType || 'unknown',
      errorMessage: errorData.errorMessage,
      errorCode: errorData.errorCode || null,
      errorLocation: errorData.errorLocation || window.location.href,
      userAction: errorData.userAction || 'unknown',
      stackTrace: errorData.stackTrace || null,
    }),
};

export const logSessionActivity = {
  started: (): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.SESSION_START, {
      sessionId: generateSessionId(),
      startTime: new Date().toISOString(),
    }),

  ended: (sessionData: SessionData): Promise<ActivityResult> =>
    logActivity(ACTIVITY_TYPES.SESSION_END, {
      sessionId: sessionData.sessionId,
      sessionDuration: sessionData.duration,
      endTime: new Date().toISOString(),
      endReason: sessionData.endReason || 'user_logout',
    }),
};

const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

let autoLoggingEnabled = false;

export const enableAutoLogging = (): void => {
  if (autoLoggingEnabled) {
    return;
  }

  autoLoggingEnabled = true;

  const logPageVisit = (): void => {
    logNavigationActivity.pageVisit({
      pageName: document.title,
      pageUrl: window.location.href,
    });
  };

  logPageVisit();

  let currentUrl = window.location.href;
  const observer = new MutationObserver(() => {
    if (window.location.href !== currentUrl) {
      currentUrl = window.location.href;
      logPageVisit();
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  window.addEventListener('error', (event: ErrorEvent) => {
    logErrorActivity.occurred({
      errorType: 'javascript_error',
      errorMessage: event.message,
      errorLocation: `${event.filename}:${event.lineno}:${event.colno}`,
      userAction: 'page_interaction',
      stackTrace: event.error?.stack,
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    logErrorActivity.occurred({
      errorType: 'promise_rejection',
      errorMessage: (event.reason as Error)?.message || String(event.reason),
      errorLocation: window.location.href,
      userAction: 'async_operation',
    });
  });
};

export const disableAutoLogging = (): void => {
  autoLoggingEnabled = false;
};

let batchQueue: BatchedActivity[] = [];
let batchTimeout: NodeJS.Timeout | null = null;

export const logActivityBatched = (
  activityType: ActivityType,
  data: ActivityData = {},
  metadata: ActivityMetadata = {}
): void => {
  batchQueue.push({ activityType, data, metadata });

  if (batchTimeout) {
    clearTimeout(batchTimeout);
  }

  batchTimeout = setTimeout(async () => {
    if (batchQueue.length === 0) {
      return;
    }

    const batch = [...batchQueue];
    batchQueue = [];

    for (const activity of batch) {
      await logActivity(activity.activityType, activity.data, activity.metadata);
    }
  }, 2000);
};

const activityLogger = {
  logActivity,
  logTenderActivity,
  logDocumentActivity,
  logAIActivity,
  logSearchActivity,
  logNavigationActivity,
  logSettingsActivity,
  logErrorActivity,
  logSessionActivity,
  enableAutoLogging,
  disableAutoLogging,
  logActivityBatched,
  ACTIVITY_TYPES,
};

export default activityLogger;
